import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';

import type {
	NatureOfCta,
	UserPickerPrincipal,
	GeneralAccessLevel,
	Principal,
} from '../controlled/commonTypes';

import { type ControllerProps, type QueriesState } from './Controller';
import type { MasterState } from './masterState';
import { makeSpecificAccessList } from './makeSpecificAccessList';
import { makePrincipalsMap, type PrincipalsMap } from './principalsMap';
import { convertUserPickerPrincipal } from './principals';
import { shouldMutationIncludeInvites } from './mutations/restrictionsTransformer';
import { ConfluenceGroupUsageType } from './queries/__types__/useQueriesShareAndRestrictDialogQuery';

export type DerivedState = {
	/** UI should not accept changes because a major change is in-flight */
	isLockedOut: boolean;

	/**  A mutation of the content permissions has been staged */
	isChanged: boolean;

	/**
	 * A mapping of a principal ID to principal data and a role, for what access
	 * that principal would nominally have if staged changes were to be applied
	 */
	principalsMap: PrincipalsMap;

	/**
	 * What the general access level of the content would be, if the staged
	 * changes (if any) were applied
	 */
	stagedGeneralAccessLevel: GeneralAccessLevel;

	/**
	 * The ordered list of ids for principals that should be appear in Specific Access
	 */
	specificAccessIds: string[];

	/**
	 * Are there principals other than the current user on the currently-staged
	 * Specific Access list?
	 */
	hasPrincipalsBesidesCurrentUser: boolean;

	/**
	 * If the user were to save now, what action would they be taking? Sharing?
	 * Saving? Or, if they're unable to save or share, "none".
	 */
	natureOfCta: NatureOfCta;

	/**
	 * People who would receive notifications, if notifications were to be sent.
	 * This is a subset of the 'additions' list, but filtered out for no-op's, and does
	 * not include the current user.
	 */
	stagedNotificationRecipients: Principal[];

	/**
	 * Indicates whether the notification checkbox is visible in the UI.
	 */
	isCheckboxVisible: boolean;

	/**
	 * Indicates whether restrictions are editable. This is false when the user should
	 * be able to see the restrictions but not edit them. Using this keeps Sharing
	 * functionality intact. This is not for times when things should be
	 * grayed out for any reason.
	 */
	isRestrictionsEditable: boolean;

	/**
	 * Indicates whether the dialog was open through the publish modal
	 */
	isPublishModalFlow: boolean;

	/**
	 * Indicates whether the remove all button is visible in the UI
	 */
	isRemoveAllButtonVisible: boolean;

	/**
	 * Indicates whether the content is an unpublished draft
	 */
	isUnpublishedDraft: boolean;

	/**
	 * Indicates whether the content will be still be unpublished after applying the changes.
	 * This occurs when changing restrictions for an unpublished draft using the
	 * ShareAndRestrictButton entry point, as opposed to the PublishModal entry point.
	 */
	willBeUnpublishedAfterApplying: boolean;

	/**
	 * Determines whether the general access select menu should be open.
	 * In rare scenarios, the menu needs to be controlled, either forced open or closed.
	 * Otherwise, the menu should manage its own state.
	 */
	shouldGeneralAccessMenuBeOpen: false | undefined;

	/**
	 * Indicates whether there is exceeded limits of users in the user picker
	 */
	exceededLimit: {
		isLimitExceeded: boolean;
		groupOverFlow: number | null;
		userOverFlow: number | null;
	};

	/**
	 * Indicates whether emails are only present in the principal stager
	 */
	areEmailsOnlyPresent: boolean;

	/**
	 * Determined sharing type.
	 */
	sharingType: 'sharing-and-applying-restrictions' | 'applying-restrictions' | 'sharing' | null;

	/**
	 * Indicates whether there is a mismatch between the queried restrictions and the staged restrictions as a result of deleted groups being filtered out
	 * Part of CONFCLOUD-80781 https://jira.atlassian.com/browse/CONFCLOUD-80781
	 */
	shouldShowDeletedGroupDetails: boolean;
	hasMismatchWithQueryRestrictions: boolean;
	hasRestrictions: boolean;
	hasInheritedRestrictions: boolean;
	hasViewRestrictions: boolean;
	isSharingToEmailsDisabled: boolean;

	/**
	 * Indicates whether to include invites in the mutation
	 */
	includeInvites: boolean;

	/**
	 * Updated group count after removals
	 */
	updatedGroupCount: number;

	/**
	 * Updated team count after removals
	 */
	updatedTeamCount: number;

	/** Total count of individual users (non-group principals) with access to the content. */
	userCount: number;
};

function getIsChanged({
	generalAccessDelta,
	specificAccessDelta: { removeAll, removals, reassignments },
	principalsStagerPrincipals,
	hasAccessRequestsChanges,
}: MasterState): boolean {
	if (removeAll && !expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')) {
		return true;
	}
	// When persistence_specific_access_gate is enabled, general access changes are applied
	// immediately (not staged), so they don't count as pending changes requiring a Save button
	if (
		generalAccessDelta !== 'none' &&
		!expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
	) {
		return true;
	}
	if (principalsStagerPrincipals.length > 0) return true;
	if (removals.size > 0) return true;
	if (reassignments.size > 0) return true;
	if (hasAccessRequestsChanges) return true;
	return false;
}

function getIsRestrictionChange({
	generalAccessDelta,
	specificAccessDelta: { removeAll, removals, reassignments },
}: MasterState): boolean {
	if (removeAll) return true;
	if (generalAccessDelta !== 'none') return true;
	if (removals.size > 0) return true;
	if (reassignments.size > 0) return true;
	return false;
}

/**
 * Reduces a list of principals to calculate user and group counts.
 */
const reducePrincipalCounts = <
	T extends { principal: { type?: Principal['type'] | 'external_user'; id: Principal['id'] } },
>({
	principalList,
	counts,
	removals = new Set<string>(),
	isRemovalPhase = false,
	isRemoveAllPhase = false,
}: {
	principalList: T[];
	counts: { users: number; groups: number };
	removals?: Set<string>;
	isRemovalPhase?: boolean;
	isRemoveAllPhase?: boolean;
}): { users: number; groups: number } => {
	return isRemoveAllPhase
		? { users: 0, groups: 0 }
		: principalList.reduce(
				(acc, { principal: { type, id } }) => {
					if (isRemovalPhase) {
						if (removals.has(id)) {
							if (type === 'user') acc.users--;
							if (type === 'group') acc.groups--;
						}
					} else {
						if (type === 'user') acc.users++;
						if (type === 'group') acc.groups++;
					}
					return acc;
				},
				{ ...counts },
			);
};

function checkForExceededLimit({
	contentRestrictions,
	specificAccessDelta,
}: {
	contentRestrictions: QueriesState['contentRestrictions'];
	specificAccessDelta: MasterState['specificAccessDelta'];
}) {
	const LIMIT = 200;
	const { removals, removeAll, stagedAdditions } = specificAccessDelta;

	// Calculate counts step by step
	const initialPrincipalCounts = reducePrincipalCounts({
		principalList: contentRestrictions.principals,
		counts: { users: 0, groups: 0 },
	});

	const countsWithRemovals = reducePrincipalCounts({
		principalList: contentRestrictions.principals,
		counts: initialPrincipalCounts,
		removals,
		isRemovalPhase: true,
		isRemoveAllPhase: removeAll,
	});

	const finalCountsWithAdditions = reducePrincipalCounts({
		principalList: stagedAdditions,
		counts: countsWithRemovals,
	});

	const calculateOverflow = (count: number): number | null =>
		count > LIMIT ? count - LIMIT : null;

	return {
		/** Indicates if either user or group limits are exceeded */
		isLimitExceeded:
			finalCountsWithAdditions.users > LIMIT || finalCountsWithAdditions.groups > LIMIT,

		/** Number of groups exceeding the limit, or null if within limit */
		groupOverFlow: calculateOverflow(finalCountsWithAdditions.groups),

		/** Number of users exceeding the limit, or null if within limit */
		userOverFlow: calculateOverflow(finalCountsWithAdditions.users),
	};
}

export function getStagedGeneralAccessLevel(
	masterState: MasterState,
	queriesState: QueriesState,
): GeneralAccessLevel {
	const { generalAccessDelta } = masterState;
	const {
		contentRestrictions: { generalAccessLevel },
	} = queriesState;

	return generalAccessDelta === 'none' ? generalAccessLevel : generalAccessDelta;
}

function getNatureOfCta({
	isChanged,
	isCheckboxVisible,
	notifyCheckedIfAvailable,
	isPublishModalFlow,
}: {
	principalsStagerPrincipals: UserPickerPrincipal[];
	isChanged: boolean;
	isCheckboxVisible: boolean;
	notifyCheckedIfAvailable: boolean;
	isPublishModalFlow: boolean;
	generalAccessDelta: MasterState['generalAccessDelta'];
}): NatureOfCta {
	if (isPublishModalFlow) {
		return 'save';
	}

	if (isChanged) {
		if (isCheckboxVisible && notifyCheckedIfAvailable) {
			return 'share';
		}
		return 'save';
	}

	return 'none';
}

function getPrincipalCountByType(
	removals: Set<string>,
	principals: Array<{
		principal: {
			type?: Principal['type'] | 'external_user';
			id: Principal['id'];
			usageType?: ConfluenceGroupUsageType | null;
		};
	}>,
	currentCount: number,
	usageType: ConfluenceGroupUsageType,
): number {
	let updatedCount = currentCount;

	for (const removalId of removals) {
		const principalToRemove = principals.find((p) => p.principal.id === removalId);

		if (principalToRemove && principalToRemove.principal.usageType === usageType) {
			updatedCount--;
		}
	}

	return updatedCount;
}

/**
 * Creates derived state. Whereas the master state is arranged to have a SSOT
 * as much as possible, the derived state calculates common derived values from
 * all state sources, for use in props.
 */
export function getDerivedState(
	masterState: MasterState,
	queriesState: QueriesState,
	controllerProps: ControllerProps,
): DerivedState {
	const {
		specificAccessDelta,
		principalsStagerPrincipals,
		notifyCheckedIfAvailable,
		shouldGeneralAccessSelectMenuBeControlled,
		generalAccessDelta,
		addMode,
	} = masterState;

	const {
		contentType,
		allowRestrictions,
		isUnpublishedDraft,
		isSuperAdmin,
		pushPublishModalRestrictions,
		hasRestrictions,
		hasInheritedRestrictions,
		hasViewRestrictions,
	} = controllerProps;

	const { edition, contentRestrictions, disableSharingToEmails, groupCount, teamCount, userCount } =
		queriesState;

	const isPublishModalFlow = Boolean(pushPublishModalRestrictions);

	const isChanged = getIsChanged(masterState);

	const stagedGeneralAccessLevel = getStagedGeneralAccessLevel(masterState, queriesState);

	//Determines if the general access level is different from the queried restrictions
	const hasMismatchWithQueryRestrictions = (() => {
		if (
			(hasRestrictions || hasInheritedRestrictions) ===
			(stagedGeneralAccessLevel === 'restricted')
		) {
			return false;
		}
		if (hasViewRestrictions === (stagedGeneralAccessLevel === 'open-can-view')) {
			return false;
		}
		if (hasRestrictions !== (stagedGeneralAccessLevel === 'open-can-edit')) {
			return true;
		}
		return true;
	})();

	const shouldShowDeletedGroupDetails =
		isSuperAdmin &&
		hasMismatchWithQueryRestrictions &&
		fg('confluence_frontend_perms_deleted_groups_treatment');

	const isRestrictionsEditable =
		contentType !== 'embed' &&
		(queriesState.userCanEditRestrictions || edition === ConfluenceEdition.FREE) &&
		allowRestrictions;

	const stagedNotificationRecipients = masterState.principalsStagerPrincipals.map(
		convertUserPickerPrincipal,
	);

	const principalsMap = makePrincipalsMap(masterState, queriesState, {
		isCtaClick: false,
		stagedGeneralAccessLevel,
	});

	const { ids, hasPrincipalsBesidesCurrentUser } = makeSpecificAccessList(
		specificAccessDelta,
		stagedGeneralAccessLevel,
		principalsMap,
		queriesState.currentUser.id,
		queriesState.contentRestrictions.principals,
		queriesState.principalsPendingAccess,
	);

	const isCheckboxVisible = (() => {
		if (isPublishModalFlow) {
			return false;
		}
		// Show the checkbox if someone is chosen in the user picker and
		// the stagedGeneralAccessLevel doesn't affect the stagedPrincipalAction.
		if (principalsStagerPrincipals.length > 0) {
			return true;
		}

		// Otherwise, it's present if there's net anyone new for the mutation
		return stagedNotificationRecipients.length > 0;
	})();

	const natureOfCta = getNatureOfCta({
		principalsStagerPrincipals,
		isChanged,
		isCheckboxVisible,
		notifyCheckedIfAvailable,
		isPublishModalFlow,
		generalAccessDelta,
	});

	const willBeUnpublishedAfterApplying = isUnpublishedDraft && !isPublishModalFlow;

	const shouldGeneralAccessMenuBeOpen = (() => {
		if (shouldGeneralAccessSelectMenuBeControlled) {
			return false;
		}
		// Returning undefined allows the menu to manage its own open/close state,
		// relinquishing control.
		return undefined;
	})();

	const exceededLimit = checkForExceededLimit({
		contentRestrictions,
		specificAccessDelta,
	});

	const areEmailsOnlyPresent = (() => {
		const hasEmails = principalsStagerPrincipals.some((principal) => principal.type === 'email');
		const hasNonEmails = principalsStagerPrincipals.some((principal) => principal.type !== 'email');
		return hasEmails && !hasNonEmails;
	})();

	const determinedSharingType = (() => {
		// Determine if the notification checkbox is visible and checked.
		const isNotificationEnabled = isCheckboxVisible && notifyCheckedIfAvailable;

		const isRestrictedOrOpenViewAsEditor =
			stagedGeneralAccessLevel === 'restricted' ||
			(stagedGeneralAccessLevel === 'open-can-view' && addMode === 'as-editor');

		const isRestrictionChange = getIsRestrictionChange(masterState);

		// When notifications are enabled and sharing is either restricted or open can view as editor
		if (isNotificationEnabled && isRestrictedOrOpenViewAsEditor) {
			return 'sharing-and-applying-restrictions';
		}

		// When notifications are disabled but sharing is still either restricted or open view as editor
		// or when there has been a change to restrictions
		if ((!isNotificationEnabled && isRestrictedOrOpenViewAsEditor) || isRestrictionChange) {
			return 'applying-restrictions';
		}

		// Check if sharing is happening at an "open-can-edit" level,
		// or at an "open-can-view" level where the added mode is "as-viewer"
		if (
			stagedGeneralAccessLevel === 'open-can-edit' ||
			(stagedGeneralAccessLevel === 'open-can-view' && addMode === 'as-viewer')
		) {
			return 'sharing';
		}

		return null;
	})();

	const userEmails = principalsStagerPrincipals.filter(
		(principal) => principal.type === 'email' || principal.type === 'external_user',
	);

	const areEmailsPresent = userEmails.length > 0;

	const principalsForCTAClick = makePrincipalsMap(masterState, queriesState, {
		isCtaClick: true,
		stagedGeneralAccessLevel,
	});

	const includeInvites = shouldMutationIncludeInvites({
		principals: principalsForCTAClick,
		removals: specificAccessDelta.removals,
		principalsPendingAccess: queriesState.principalsPendingAccess,
		removeAll: specificAccessDelta.removeAll,
	});
	const updatedGroupCount = getPrincipalCountByType(
		specificAccessDelta.removals,
		contentRestrictions.principals,
		groupCount,
		ConfluenceGroupUsageType.USERBASE_GROUP,
	);

	const updatedTeamCount = getPrincipalCountByType(
		specificAccessDelta.removals,
		contentRestrictions.principals,
		teamCount,
		ConfluenceGroupUsageType.TEAM_COLLABORATION,
	);
	return {
		shouldShowDeletedGroupDetails,
		hasRestrictions,
		hasInheritedRestrictions,
		hasViewRestrictions,
		hasMismatchWithQueryRestrictions,
		isLockedOut: false, // TODO: will be masterState.isSaving,
		isRestrictionsEditable,
		isChanged,
		principalsMap,
		stagedGeneralAccessLevel,
		specificAccessIds: ids,
		hasPrincipalsBesidesCurrentUser,
		natureOfCta,
		stagedNotificationRecipients,
		isCheckboxVisible,
		isPublishModalFlow,
		isUnpublishedDraft,
		willBeUnpublishedAfterApplying,
		isRemoveAllButtonVisible: hasPrincipalsBesidesCurrentUser,
		shouldGeneralAccessMenuBeOpen,
		exceededLimit,
		areEmailsOnlyPresent,
		sharingType: determinedSharingType,
		isSharingToEmailsDisabled: disableSharingToEmails && areEmailsPresent,
		includeInvites,
		updatedGroupCount,
		updatedTeamCount,
		userCount,
	};
}
